import React, { useEffect, useState, useCallback } from "react";
import {
  Box, VStack, HStack, Heading, Text, Avatar, Button,
  Divider, Spinner, Progress, Flex, Badge, Alert, AlertIcon,
  Tab, TabList, TabPanel, TabPanels, Tabs,
  Input, SimpleGrid, Stack
} from "@chakra-ui/react";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";

// Avatar Imports
import avatar1 from "../images/avatars/avatar1.png";
import avatar2 from "../images/avatars/avatar2.png";
import avatar3 from "../images/avatars/avatar3.png";
import avatar4 from "../images/avatars/avatar4.png";
import avatar5 from "../images/avatars/avatar5.png";
import avatar6 from "../images/avatars/avatar6.png";
import avatar7 from "../images/avatars/avatar7.png";
import avatar8 from "../images/avatars/avatar8.png";
import avatar9 from "../images/avatars/avatar9.png";
import avatar10 from "../images/avatars/avatar10.png";
import avatar11 from "../images/avatars/avatar11.png";
import avatar12 from "../images/avatars/avatar12.png";
import logoImg from '../images/AiM4u Registered Logo (1).png';
import tickImg from '../images/check.png';

const avatarMap = {
  "avatar1.png": avatar1,
  "avatar2.png": avatar2,
  "avatar3.png": avatar3,
  "avatar4.png": avatar4,
  "avatar5.png": avatar5,
  "avatar6.png": avatar6,
  "avatar7.png": avatar7,
  "avatar8.png": avatar8,
  "avatar9.png": avatar9,
  "avatar10.png": avatar10,
  "avatar11.png": avatar11,
  "avatar12.png": avatar12,
};

const Dashboard = () => {
  const { userId } = useSelector((state) => state.user);
  const [userProfile, setUserProfile] = useState({});
  const [appliedInternships, setAppliedInternships] = useState([]);
  const [internshipProgressData, setInternshipProgressData] = useState([]);
  const [loading, setLoading] = useState(true);
  const accessToken = sessionStorage.getItem("accessToken");
  const localAvatar = localStorage.getItem("selectedAvatar") || "avatar1.png";
  const normalizedAvatar = avatarMap[localAvatar] || avatar1;

  const fetchUserProfile = useCallback(async () => {
    try {
      const { data } = await axios.get("https://gyanbackend.aim4u.co.in/profile/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setUserProfile({
        Fname: data.Fname,
        Lname: data.Lname,
        phone_number: data.phone_number,
        profile_photo: localAvatar,
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  }, [accessToken, localAvatar]);

  const fetchAppliedInternships = useCallback(async () => {
    try {
      const { data } = await axios.get(
        "https://gyanbackend.aim4u.co.in/intern/applied-internships/",
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      // Filter only approved internships
      const approvedInternships = data.filter(internship => internship.status === "Approved");
      setAppliedInternships(approvedInternships);
    } catch (error) {
      console.error("Error fetching applied internships:", error);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const fetchInternshipProgress = useCallback(async () => {
    try {
      const { data } = await axios.get(
        "https://gyanbackend.aim4u.co.in/intern/progress/",
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      setInternshipProgressData(data);
    } catch (error) {
      console.error("Error fetching internship progress:", error);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchUserProfile();
    fetchAppliedInternships();
    fetchInternshipProgress();
  }, [fetchUserProfile, fetchAppliedInternships, fetchInternshipProgress]);

  const generateReceipt = (intern) => {
    const doc = new jsPDF();
    const logo = new Image();
    logo.src = logoImg;
    const tick = new Image();
    tick.src = tickImg;

    logo.onload = () => {
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.addImage(logo, 'PNG', 20, 10, 20, 20);
      doc.setFontSize(20);
      doc.setTextColor(0, 0, 139);
      doc.setFont("helvetica", "bold");
      const gyanText = "GYAN";
      const gyanWidth = doc.getTextWidth(gyanText);
      doc.text(gyanText, (pageWidth - gyanWidth) / 2, 18);
      doc.setFontSize(16);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 128, 0);
      const subText = "AiM4u Software Solutions Private Limited";
      const subTextWidth = doc.getTextWidth(subText);
      doc.text(subText, (pageWidth - subTextWidth) / 2, 24);
      doc.setDrawColor(180);
      doc.line(20, 35, 190, 35);
      doc.setFontSize(16);
      doc.setTextColor("#2F855A");
      doc.text("Internship Payment Receipt", 105, 45, { align: "center" });
      const boxY = 55;
      doc.setLineWidth(0.2);
      doc.setDrawColor(0);
      doc.rect(20, boxY, 170, 80);
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      const details = [
        ["Name", `${userProfile.Fname} ${userProfile.Lname}`],
        ["Internship Title", intern.Internship_title],
        ["Duration", `${intern.duration_weeks} weeks`],
        ["Transaction ID", intern.transaction_id || "N/A"],
        ["Payment Status", "Paid"],
        ["Date", intern.payment_date ? new Date(intern.payment_date).toLocaleDateString() : "N/A"],
        // ["Date", new Date().toLocaleDateString()],
      ];
      let y = boxY + 10;
      details.forEach(([label, value]) => {
        doc.text(`${label}:`, 28, y);
        doc.text(`${value}`, 80, y);
        y += 10;
      });
      doc.text("Fees Paid:", 28, y);
      doc.text(`INR ${intern.fees}`, 80, y);
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      doc.text("Thank you for choosing our platform!", 28, y + 15);
      doc.line(20, 150, 190, 150);
      tick.onload = () => {
        doc.addImage(tick, 'PNG', 25, 158, 6, 6);
        doc.setFontSize(10);
        doc.setTextColor("#1E90FF");
        doc.text("Digitally verified by the Gyan official authority", 35, 163);
        const filename = `${userProfile.Fname} ${userProfile.Lname}_internship payment receipt.pdf`;
        doc.save(filename);
      };
    };
  };

  if (loading) {
    return (
      <Box p={20} textAlign="center">
        <Spinner size="xl" color="green.500" />
      </Box>
    );
  }

  return (
    <Box bgGradient="linear(to-r, blue.100, green.50, green.100)" minH="100vh" display="flex" flexDirection="column">
      <NavBar />

      {appliedInternships.length === 0 ? (
        <Box flex="1" display="flex" alignItems="center" justifyContent="center" p={8}>
          <Box bg="white" p={6} borderRadius="lg" boxShadow="lg" textAlign="center" w={{ base: "90%", md: "sm" }}>
            <Heading size="md" color="red.500" mb={4}>
            No Approved Internships
            </Heading>
            <Text color="gray.600" mb={6}>
              You don't have any approved internships yet. Check back later or apply for new ones.
            </Text>
            <Link to="/internship">
              <Button colorScheme="blue">Browse Internships</Button>
            </Link>
          </Box>
        </Box>
      ) : (
        <Flex direction={{ base: "column", lg: "row" }} align="stretch" flex="1">
          {/* Sidebar */}
          <Box w={{ base: "100%", lg: "20%" }} bg="blue.900" color="white" p={5} boxShadow="md">
            <VStack spacing={4} align="center">
              <Avatar size="xl" src={normalizedAvatar} />
              <Heading size="md" textAlign="center">
                {userProfile.Fname || "Guest"} {userProfile.Lname || ""}
              </Heading>
              <Divider borderColor="whiteAlpha.500" />
              <Text fontWeight="bold" color="green.300">Active Internship: {appliedInternships.length}</Text>
              <Link to="/profile">
                <Button colorScheme="blue" size="sm">Edit Profile</Button>
              </Link>
            </VStack>
          </Box>

         
          <Box w={{ base: "100%", lg: "80%" }} p={8} bg="white">
            <Heading size="xl" fontWeight="bold" mb={6} color="green.600" textAlign="center">
              My Dashboard
            </Heading>

            <Tabs variant="soft-rounded" colorScheme="green">
              <TabList mb={6} overflowX="auto" pb={2}>
                {appliedInternships.map((intern) => (
                  <Tab key={intern.id} whiteSpace="nowrap">
                    {intern.Internship_title}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {appliedInternships.map((intern) => {
                  const progress = internshipProgressData.find(p => p.id === intern.id);
                  return (
                    <TabPanel key={intern.id}>
                      <InternshipDetails
                        intern={intern}
                        userProfile={userProfile}
                        generateReceipt={generateReceipt}
                        progress={progress}
                      />
                    </TabPanel>
                  );
                })}
              </TabPanels>
            </Tabs>
          </Box>
        </Flex>
      )}
    </Box>
  );
};

const InternshipDetails = ({ intern, userProfile, generateReceipt, progress }) => {
  const [todoList, setTodoList] = useState([]);
  const [newTask, setNewTask] = useState('');
  const isPaid = intern.payment_status === "COMPLETED";
  const hasPaymentDate = !!intern.payment_date;

  const COLORS = ["#2F855A", "#FFCCCC"];

  const handleAddTask = () => {
    if (newTask.trim() !== '') {
      setTodoList([...todoList, newTask.trim()]);
      setNewTask('');
    }
  };

  const handleRemoveTask = (idx) => {
    setTodoList(todoList.filter((_, index) => index !== idx));
  };

  const progressData = [
    { name: "Completed", value: progress ? progress.progress_percent : 0 },
    { name: "Remaining", value: progress ? 100 - progress.progress_percent : 100 },
  ];

  const daysCompleted = progress ? progress.days_passed : 0;
  const totalDays = progress ? progress.total_days : intern.duration_weeks * 7;

  return (
    <VStack spacing={6} align="stretch">
      {/* Internship Details */}
      <Box bgGradient="linear(to-r, blue.100, green.50)" p={5} boxShadow="md" borderRadius="lg">
        <Badge colorScheme="green" mb={2}>
          Approved
        </Badge>
        <Text fontWeight="bold" fontSize="xl">{intern.Internship_title}</Text>
        <Text color="gray.600" mb={2}>
          <strong>Duration:</strong> {intern.duration_weeks} weeks | <strong>Fees:</strong> ₹ {intern.fees}
        </Text>
        <Text color="gray.600">
          <strong>Payment Date:</strong> {intern.payment_date ? new Date(intern.payment_date).toLocaleDateString() : 'N/A'} |
          <strong> Status:</strong> {isPaid ? "Payment Completed" : "Payment Pending"}
        </Text>

        {/* Progress Stepper */}
        <Box mt={5}>
          <Flex justify="space-between" align="center" direction={{ base: "column", md: "row" }}>
            {/* Step 1: Approved */}
            <Box flex={{ md: "0.3" }} mx={{ md: 1 }} position="relative" mb={{ base: 2, md: 0 }}>
              <Box
                height="8px"
                bg="linear-gradient(90deg, #34D399, #10B981)"
                borderRadius="full"
              />
              <Text position="absolute" top="-8px" right="-5px" fontSize="lg" color="green.500">
                ✅
              </Text>
              <Text mt={2} textAlign="center" fontSize="sm" fontWeight="medium" color="green.600">
                Approved
              </Text>
            </Box>

            {/* Step 2: Payment */}
            <Box flex={{ md: "0.3" }} mx={{ md: 1 }} position="relative" mb={{ base: 2, md: 0 }}>
              <Box
                height="8px"
                bg={isPaid ? "linear-gradient(90deg, #34D399, #10B981)" : "linear-gradient(90deg, #F87171, #EF4444)"}
                borderRadius="full"
              />
              {isPaid && (
                <Text position="absolute" top="-8px" right="-5px" fontSize="lg" color="green.500">
                  ✅
                </Text>
              )}
              <Text mt={2} textAlign="center" fontSize="sm" fontWeight="medium" color={isPaid ? "green.600" : "gray.600"}>
                {isPaid ? "Payment Completed" : "Payment Pending"}
              </Text>
            </Box>

            {/* Step 3: In Progress */}
            <Box flex={{ md: "0.3" }} mx={{ md: 1 }} position="relative">
              <Box
                height="8px"
                bg={hasPaymentDate && isPaid ? `linear-gradient(90deg, #34D399 ${progress ? progress.progress_percent : 0}%, gray.300 ${progress ? progress.progress_percent : 0}%)` : "gray.300"}
                borderRadius="full"
              />
              {hasPaymentDate && isPaid && (
                <Text position="absolute" top="-8px" right="-5px" fontSize="lg" color="green.500">
                  ✅
                </Text>
              )}
              <Text mt={2} textAlign="center" fontSize="sm" fontWeight="medium" color={hasPaymentDate && isPaid ? "green.600" : "gray.600"}>
                {hasPaymentDate && isPaid ? "In Progress" : "Pending Start"}
              </Text>
            </Box>
          </Flex>
        </Box>

        {/* Download Receipt */}
        {isPaid && intern.payment_date && (
          <Box mt={5} textAlign="center">
            <Button
              size="sm"
              colorScheme="green"
              onClick={() => generateReceipt(intern)}
            >
              Download Payment Receipt
            </Button>
          </Box>
        )}
      </Box>

      {/* Charts and Tasks Section */}
      <Stack direction={{ base: "column", lg: "row" }} align="stretch" spacing={8}>
        {/* Progress Chart */}
        <Box bgGradient="linear(to-r, blue.100, green.50, green.100)" p={5} boxShadow="md" borderRadius="lg" w={{ base: "100%", lg: "50%" }}>
          <Box bg="blue.700" color="white" p={3} borderRadius="md" mb={4} textAlign="center">
            <Heading size="md">Internship Progress</Heading>
          </Box>
          <Flex justify="center">
            <PieChart width={300} height={300}>
              <Pie
                data={progressData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                fill="#2F855A"
                paddingAngle={5}
                dataKey="value"
              >
                {progressData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
            </PieChart>
          </Flex>
          <Text textAlign="center" mt={2} fontWeight="medium">
            {hasPaymentDate ? `${daysCompleted} days completed of ${totalDays} total days (${progress ? progress.progress_percent : 0}%)` : 'Progress will start after payment'}
          </Text>
        </Box>

        {/* Weekly Tasks */}
        <Box bgGradient="linear(to-r, blue.100, green.50, green.100)" p={5} boxShadow="md" borderRadius="lg" w={{ base: "100%", lg: "50%" }}>
          <Box bg="blue.700" color="white" p={3} borderRadius="md" mb={4} textAlign="center">
            <Heading size="md">Weekly Tasks</Heading>
          </Box>
          <VStack spacing={3} align="stretch">
            {todoList.map((item, idx) => (
              <Box key={idx} p={2} bg="white" borderRadius="md" boxShadow="sm" display="flex" justifyContent="space-between" alignItems="center">
                <Text>{item}</Text>
                <Button colorScheme="red" size="sm" onClick={() => handleRemoveTask(idx)}>
                  Remove
                </Button>
              </Box>
            ))}
            <HStack>
              <Input
                placeholder="Add task..."
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
              />
              <Button colorScheme="blue" onClick={handleAddTask}>
                Add
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Stack>
    </VStack>
  );
};

export default Dashboard;